// src/UI/Button.jsx

import React from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { styled } from '@mui/system';

// Import your global COLORS constant
import COLORS from '../../constants/colors';

// --- Spinner Component (MUI CircularProgress) ---
const Spinner = () => (
  <CircularProgress
    size={20} // Adjust size as needed
    color="inherit" // Inherit color from the button's text
    sx={{ marginRight: 1 }} // Add some space if text is present
  />
);

// --- ButtonContent Helper ---
const ButtonContent = ({ loading, loadingText, icon, iconPosition, children }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner />
        {loadingText && <span>{loadingText}</span>}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
      {iconPosition === 'left' && icon}
      <span>{children}</span>
      {iconPosition === 'right' && icon}
    </Box>
  );
};

// --- Base Button Factory Function ---
const makeMuiButton = ({
  backgroundColor,
  textColor,
  borderColor,
  hoverBackgroundColor,
  hoverTextColor,
  hoverBorderColor,
  focusRingColor,
}) => {
  return ({
    children,
    loading = false,
    loadingText,
    disabled = false,
    onClick,
    icon,
    iconPosition = 'left',
    className = '',
    type = 'button',
    ...props
  }) => {
    return (
      <Button
        type={type}
        disabled={disabled || loading}
        onClick={onClick}
        className={className}
        variant="contained"
        sx={{
          py: 0.8,
          px: 2.5,
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',

          backgroundColor: backgroundColor,
          color: textColor,
          border: borderColor ? `1px solid ${borderColor}` : 'none',

          '&:hover': {
            backgroundColor: hoverBackgroundColor || backgroundColor,
            color: hoverTextColor || textColor,
            borderColor: hoverBorderColor || borderColor,
            boxShadow: 'none',
          },

          '&.Mui-disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: backgroundColor, // Maintain background color when disabled
            color: textColor, // Maintain text color when disabled
            border: borderColor ? `2px solid ${borderColor}` : 'none', // Maintain border when disabled
          },

          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 4px ${focusRingColor}`,
          },

          ...props.sx,
        }}
        {...props}
      >
        <ButtonContent
          loading={loading}
          loadingText={loadingText}
          icon={icon}
          iconPosition={iconPosition}
        >
          {children}
        </ButtonContent>
      </Button>
    );
  };
};

// --- Exported Button Components ---

/**
 * Primary Button component.
 * Ideal for main actions (e.g., Submit, Save, Confirm).
 */
export const PrimaryButton = makeMuiButton({
  backgroundColor: COLORS.primary,
  textColor: COLORS.white,
  borderColor: 'transparent',
  hoverBackgroundColor: COLORS.primaryDark,
  focusRingColor: COLORS.primary + '33', // Primary with some transparency for ring
});


export const OutlinedButton = makeMuiButton({
  backgroundColor: 'transparent', // No background for outlined
  textColor: COLORS.primary,      // Primary text color
  borderColor: COLORS.primary,    // Primary border color
  hoverBackgroundColor: COLORS.primary + '1A', // A slight tint of primary on hover (e.g., 10% opacity)
  hoverTextColor: COLORS.primary, // Keep text primary on hover
  hoverBorderColor: COLORS.primary, // Keep border primary on hover
  focusRingColor: COLORS.primary + '33', // Primary with some transparency for ring
});

/**
 * Cancel Button component.
 * Ideal for secondary actions or dismissing (e.g., Cancel, Close).
 */
export const CancelButton = makeMuiButton({
  backgroundColor: COLORS.white,
  textColor: COLORS.neutral800,
  borderColor: COLORS.neutral400,
  hoverBackgroundColor: COLORS.neutral100,
  hoverBorderColor: COLORS.neutral800,
  focusRingColor: COLORS.neutral400 + '33',
});

/**
 * Delete Button component.
 * Clearly indicates a destructive action.
 */
export const DeleteButton = makeMuiButton({
  backgroundColor: COLORS.error,
  textColor: COLORS.white,
  borderColor: 'transparent',
  hoverBackgroundColor: COLORS.redDark,
  focusRingColor: COLORS.error + '33',
});

/**
 * UploadButton component.
 */
export const UploadButton = ({
  children = 'Upload',
  loading = false,
  loadingText = 'Loading...',
  disabled = false,
  onFilesSelected,
  multiple = false,
  accept = '*',
  icon,
  iconPosition = 'left',
  className = '',
  ...props
}) => {
  const fileInputRef = React.useRef(null);

  const handleClick = () => {
    if (!disabled && !loading) {
      fileInputRef.current?.click();
    }
  };

  const handleChange = (e) => {
    if (onFilesSelected) {
      onFilesSelected(e.target.files);
    }
    e.target.value = null;
  };

  return (
    <>
      <Button
        type="button"
        disabled={disabled || loading}
        onClick={handleClick}
        className={className}
        variant="contained"
        sx={{
          py: 1,
          px: 2,
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          backgroundColor: COLORS.primary,
          color: COLORS.white,
          '&:hover': {
            backgroundColor: COLORS.primaryDark,
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            opacity: 0.6,
            backgroundColor: COLORS.primary,
            color: COLORS.white,
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 4px ${COLORS.primary + '33'}`,
          },
          ...props.sx,
        }}
        {...props}
      >
        <ButtonContent
          loading={loading}
          loadingText={loadingText}
          icon={icon}
          iconPosition={iconPosition}
        >
          {children}
        </ButtonContent>
      </Button>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleChange}
        multiple={multiple}
        accept={accept}
        style={{ display: 'none' }}
      />
    </>
  );
};